import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as pokemonCardOwnershipService from '../services/pokemonCardOwnershipService';
import type { CardOwnershipInput } from '../services/pokemonCardOwnershipService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to fetch all card ownerships for a set
 */
export function useCardOwnerships(
  setId?: string,
  includePricing: boolean = false,
  source: 'master' | 'collection' = 'master'
) {
  const { isAuthenticated, user } = useAuth();
  return useQuery({
    queryKey: ['card-ownerships', setId, includePricing, source],
    queryFn: async () => {
      console.log('[useCardOwnerships] Fetching ownerships for setId:', setId, 'includePricing:', includePricing);
      console.log('[useCardOwnerships] Auth state:', {
        isAuthenticated,
        userId: user?.userId,
      });

      if (!isAuthenticated) {
        console.warn(
          '[useCardOwnerships] Not authenticated, returning empty array',
        );
        return [];
      }

      const result = await pokemonCardOwnershipService.getUserCardOwnerships(setId, includePricing, source);
      console.log('[useCardOwnerships] Fetched', result.length, 'ownerships');
      return result;
    },
    staleTime: 0, // Always fetch fresh data (no caching)
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 1,
    enabled: isAuthenticated,
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: false, // Disabled to prevent unexpected refetches when interacting with dev tools
  });
}

/**
 * Hook to fetch set progress (owned count per set)
 */
export function useSetProgress() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['sets', 'progress'],
    queryFn: () => pokemonCardOwnershipService.getUserSetProgress(),
    networkMode: 'always', // Always refetch when invalidated (critical for real-time progress updates)
    retry: 1,
    enabled: isAuthenticated,
  });
}

/**
 * Hook to fetch ownership for a single card
 */
export function useCardOwnership(cardId: string) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['card-ownership', cardId],
    queryFn: () => pokemonCardOwnershipService.getCardOwnership(cardId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 1,
    enabled: isAuthenticated && !!cardId,
  });
}

/**
 * Hook to update card ownership
 */
export function useUpdateCardOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardId,
      data,
    }: {
      cardId: string;
      data: CardOwnershipInput;
    }) => pokemonCardOwnershipService.upsertCardOwnership(cardId, data),
    onSuccess: (updatedOwnership, variables) => {
      // Invalidate and refetch card ownership queries
      queryClient.invalidateQueries({
        queryKey: ['card-ownership', variables.cardId],
      });
      queryClient.invalidateQueries({ queryKey: ['card-ownerships'] });
      queryClient.invalidateQueries({ queryKey: ['sets', 'progress'] });

      // Update the cache with the new data
      queryClient.setQueryData(['card-ownership', variables.cardId], updatedOwnership);
    },
  });
}

/**
 * Hook to toggle card ownership (simple owned/not owned)
 */
export function useToggleCardOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) =>
      pokemonCardOwnershipService.toggleCardOwnership(cardId),
    onMutate: async (cardId) => {
      console.log('[useToggleCardOwnership] onMutate - cardId:', cardId);
    },
    onSuccess: (data, cardId) => {
      console.log(
        '[useToggleCardOwnership] Toggle success, cardId:',
        cardId,
        'data:',
        data,
      );

      // Manually update the list cache FIRST if we have the set ID
      if (data && data.setId) {
        queryClient.setQueryData<
          import('../services/pokemonCardOwnershipService').CardOwnership[]
        >(['card-ownerships', data.setId], (old) => {
          console.log(
            '[useToggleCardOwnership] Updating cache for setId:',
            data.setId,
            'old:',
            old,
          );
          if (!old) return [data];
          const exists = old.find((o) => o.cardId === cardId);
          if (exists) {
            const updated = old.map((o) => (o.cardId === cardId ? data : o));
            console.log('[useToggleCardOwnership] Updated cache:', updated);
            return updated;
          }
          return [...old, data];
        });

        // Invalidate setProgress to refetch from backend (backend handles the count correctly)
        queryClient.invalidateQueries({ queryKey: ['sets', 'progress'] });
      }

      // Update the cache for the single card
      queryClient.setQueryData(['card-ownership', cardId], data);

      console.log('[useToggleCardOwnership] Queries updated');
    },
    onError: (error, cardId) => {
      console.error(
        '[useToggleCardOwnership] Error - cardId:',
        cardId,
        'error:',
        error,
      );
    },
  });
}

/**
 * Hook to toggle card wishlist status
 */
export function useToggleCardWishlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) =>
      pokemonCardOwnershipService.toggleCardWishlist(cardId),
    onSuccess: (data, cardId) => {
      // Update both the list and single card cache
      if (data && data.setId) {
        queryClient.invalidateQueries({ queryKey: ['card-ownerships', data.setId] });
      }
      queryClient.setQueryData(['card-ownership', cardId], data);
    },
  });
}

/**
 * Hook to toggle card favorite status
 */
export function useToggleCardFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) =>
      pokemonCardOwnershipService.toggleCardFavorite(cardId),
    onSuccess: (data, cardId) => {
      // Update both the list and single card cache
      if (data && data.setId) {
        queryClient.invalidateQueries({ queryKey: ['card-ownerships', data.setId] });
      }
      queryClient.setQueryData(['card-ownership', cardId], data);
    },
  });
}

/**
 * Hook to delete card ownership
 */
export function useDeleteCardOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) =>
      pokemonCardOwnershipService.deleteCardOwnership(cardId),
    onSuccess: (_, cardId) => {
      // Invalidate and refetch card ownership queries
      queryClient.invalidateQueries({ queryKey: ['card-ownership', cardId] });
      queryClient.invalidateQueries({ queryKey: ['card-ownerships'] });
      queryClient.invalidateQueries({ queryKey: ['sets', 'progress'] });

      // Remove from cache
      queryClient.setQueryData(['card-ownership', cardId], null);
    },
  });
}

/**
 * Hook to bulk update card ownerships
 */
export function useBulkUpdateCardOwnerships() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      ownerships: Array<{ cardId: string } & CardOwnershipInput>
    ) =>
      pokemonCardOwnershipService.bulkUpsertCardOwnerships(ownerships),
    onSuccess: (results) => {
      // Invalidate all card ownership queries
      queryClient.invalidateQueries({ queryKey: ['card-ownerships'] });
      queryClient.invalidateQueries({ queryKey: ['card-ownership'] });
      queryClient.invalidateQueries({ queryKey: ['sets', 'progress'] });
    },
  });
}

/**
 * Hook to get ownerships as a map for easy lookup
 */
export function useCardOwnershipsMap(setId?: string, source: 'master' | 'collection' = 'master') {
  const { data: ownerships = [], ...rest } = useCardOwnerships(setId, true, source);

  const ownershipsMap = React.useMemo(() => {
    if (!ownerships || ownerships.length === 0) return {};
    return pokemonCardOwnershipService.ownershipsToMap(ownerships);
  }, [ownerships]);

  return {
    ownershipsMap,
    ownerships,
    ...rest,
  };
}
